import { isUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { type BDCore } from '@bizzdesign/sdk-knowledgegraph';
import {
  type AttributeCategoryDefinitionDto,
  type AttributeCategoryDto,
  type AttributeCustomColors,
  type AttributeRangeValue,
  type AttributeStateCategory,
  type AttributeStateSubCategory,
  type AttributeStateSubCategoryItem,
  type AttributeTreeNode,
  type AttributeType,
  type AttributeValueTypes,
  type AttributeWithGraphicIds,
  type BaseObjectDto,
  colorLegendNoValueKey,
  type GraphicIdParam,
  noValueDefaultRangeData,
  type ObjectDto,
  type RepositoryLoadedElementIdPair,
} from '@bizzdesign/shared/models';
import { getBDCoreObjectTypeIcon } from '@bizzdesign/shared/utils';
import { isEqual } from 'lodash-es';
import { mergeArrays } from '../common/array.utils';
import { formatName } from '../common/formatting.utils';

export const maxNumberOfCustomColors = 21;
const alignRightValueTypes: Set<AttributeType> = new Set<AttributeType>([
  'integer',
  'money',
  'real',
]);

type AttributeCallbackFn<T> = (
  category: AttributeStateCategory,
  attribute: AttributeStateSubCategoryItem,
  graphicIds?: T[],
  groupedGraphicIdsAndAttributesByKey?: Record<string, AttributeWithGraphicIds>,
  attributeCategoryDefinitions?: AttributeCategoryDefinitionDto[],
) => AttributeStateSubCategoryItem;

export const generateAttributeCategories = (
  response: ObjectDto[],
  attributeCategoryDefinitions: AttributeCategoryDefinitionDto[],
): AttributeStateCategory[] => {
  return response.reduce((acc, curr) => {
    const attrCategories = curr.attributeCategories;
    if (attrCategories?.length) {
      if (curr.type && !acc.some((v) => v.type === curr.type)) {
        acc.push({ subcategories: [], type: curr.type });
      }
      const index = acc.findIndex((v) => v.type === curr.type);
      if (index >= 0) {
        const subcategories = attrCategories.map((c) => {
          const categoryDef = attributeCategoryDefinitions.find(
            (cat) => cat.id === c.id,
          );
          return toAttributeStateSubCategory(c, categoryDef);
        });
        const newSubcategories = handleExistingCategoryAttributes(
          subcategories,
          acc[index].subcategories,
        );

        acc[index].subcategories = [...newSubcategories];
      }
    }
    return acc;
  }, [] as AttributeStateCategory[]);
};

/**
 * Merges the current subcategories with the existing ones and
 * updating the attributes of existing subcategories without mutating arguments.
 *
 * @param subcategories - An array of existing subcategories to be merged.
 * @param currSubcategories - The current array of subcategories to be updated.
 * @returns `AttributeStateSubCategory[]` with the merged subcategories.
 */
const handleExistingCategoryAttributes = (
  subcategories: AttributeStateSubCategory[],
  currSubcategories: AttributeStateSubCategory[],
): AttributeStateSubCategory[] => {
  return subcategories.reduce(
    (acc, subcategory) => {
      const findSubcategory = currSubcategories.find(
        (a) => a.name === subcategory.name,
      );

      // If no matching subcategory exists, add the existing subcategory to the result array, otherwise,
      // remove duplicates and add new attributes to the existing subcategory

      if (!findSubcategory) {
        return [...acc, subcategory];
      }

      const newAttributes = subcategory.attributes.filter(
        (attr) =>
          !subcategory.attributes.some(
            (existingAttr) => existingAttr.name === attr.name,
          ),
      );

      return acc.map((sub) => {
        if (sub.name === subcategory.name) {
          return {
            ...sub,
            attributes: [...sub.attributes, ...newAttributes],
          };
        }

        return sub;
      });
    },
    [...currSubcategories],
  );
};

const toAttributeStateSubCategory = (
  c: AttributeCategoryDto,
  categoryDef: AttributeCategoryDefinitionDto | undefined,
): AttributeStateSubCategory => ({
  name: categoryDef?.name ?? c.name ?? c.id,
  attributes: c.attributes.map((v) => ({
    name: v.name,
    type: v.type.toLowerCase() as AttributeType,
  })),
});

/**
 * Maps attribute categories structure into the structure for tree component
 * @param categories - an array of attribute categories
 * @returns - an array of tree nodes
 */
export const toAttributeTreeStructure = (
  categories: AttributeStateCategory[],
): AttributeTreeNode[] => {
  return categories.map((category) => ({
    key: category.type,
    label: formatName(category.type.split('.')[1]),
    icon: getBDCoreObjectTypeIcon(category.type as BDCore.ElementType),
    selectable: false,
    children: category.subcategories.map((subcategory) => ({
      key: `${subcategory.name}-${category.type}`,
      label: subcategory.name,
      selectable: false,
      children: subcategory.attributes.map((attribute) => ({
        key: `${attribute.name}-${subcategory.name}-${category.type}`,
        label: attribute.name,
        type: 'leaf',
        selectable: true,
        valueType: attribute.type,
        range: attribute.range,
      })),
    })),
  }));
};

/**
 * Retrieves unique custom colors from the provided colors map using a specific attribute key.
 * The current colors are preserved to ensure that previously added colors are not lost.
 * This allows for extracting the colors associated with the selected attribute.
 * @param currentColors - String[] - current custom colors from the state
 * @param colorsMap - AttributeCustomColors - custom colors map
 * @param attributeKey - String - attribute tree key
 * @returns Array of hex codes
 */
export const extractCustomColorsFromMap = (
  currentColors: string[],
  colorsMap: AttributeCustomColors | undefined,
  attributeKey: string,
): string[] => {
  const attributeColors = colorsMap?.[attributeKey];
  if (!attributeKey || !attributeColors) {
    return [];
  }

  const mergedColors = [
    ...currentColors,
    ...Object.values(attributeColors)
      .filter((v) => v.isCustom)
      .map((v) => v.color),
  ];
  const customColors = Array.from(new Set(mergedColors));

  if (customColors.length > maxNumberOfCustomColors) {
    customColors.splice(0, customColors.length - maxNumberOfCustomColors);
  }

  return customColors;
};

/**
 * Get all attributes and replace semanticId with graphicId
 * @param response - all attributes
 * @param semanticGraphicIdPairs - pairs of semantic and graphic ids
 * @returns - mapped attributes with graphic id
 */
export const getAllAttributes = (
  response: ObjectDto[],
  semanticGraphicIdPairs: RepositoryLoadedElementIdPair[],
): AttributeWithGraphicIds[] => {
  return response.flatMap(({ type, id, attributeCategories }) => {
    // find all graphic ids based on semantic id
    if (!attributeCategories?.length) return [];
    const graphicIds = semanticGraphicIdPairs
      .filter((pair) => pair.semanticId === id)
      ?.map((pair) => pair.graphicId);

    return attributeCategories.flatMap((category) =>
      category.attributes.map((attr) => {
        const isReferenceObjectType =
          attr.type === 'reference' && !isUUID(attr.value);
        const objectValue = attr.value as BaseObjectDto;
        const refObjName = objectValue?.name || undefined;
        let displayValue: AttributeWithGraphicIds['displayValue'] = undefined;
        if (attr.type === 'enum') {
          displayValue = attr.displayValue ?? undefined;
        } else if (isReferenceObjectType) {
          displayValue = refObjName;
        }
        const refObjId = objectValue.id || '';
        const result: AttributeWithGraphicIds = {
          elementType: type,
          attributeType: attr.type.toLowerCase() as AttributeType,
          attributeName: attr.name,
          value: isReferenceObjectType
            ? refObjId
            : (attr.value as AttributeValueTypes),
          graphicIds: [...graphicIds],
          isVisible: true,
          currency: attr.type === 'money' ? attr.currency : undefined,
          isColorPickerVisible: false,
          displayValue,
        };
        return result;
      }),
    );
  });
};

/**
 * Groups all graphicIds by attribute values and attributes by key
 * @param allAttributes - list of all attributes
 * @returns grouped attributes and graphicIds
 */
export const groupGraphicIdsAndAttributesByKey = (
  allAttributes: AttributeWithGraphicIds[],
): Record<string, AttributeWithGraphicIds> => {
  return allAttributes.reduce<Record<string, AttributeWithGraphicIds>>(
    (acc, curr) => {
      const key = `${curr.elementType}-${curr.attributeName}-${curr.value}`;
      if (!acc[key]) {
        acc[key] = {
          elementType: curr.elementType,
          attributeType: curr.attributeType,
          attributeName: curr.attributeName,
          value: curr.value,
          graphicIds: [],
          isVisible: curr.isVisible,
          currency: curr.currency,
          isColorPickerVisible: curr.isColorPickerVisible,
          displayValue: curr.displayValue,
        };
      }
      acc[key].graphicIds = [...acc[key].graphicIds, ...curr.graphicIds];
      return acc;
    },
    {},
  );
};

/**
 * Goes through categories -> subcategories -> attributes and applies the passed function to each attribute
 * @param categories
 * @param attributeCallbackFn function applied to each entry in the attributes array, should return an attribute object
 * @returns Updated categories based on the callback
 */
export const applyCallbackToAttributes = (
  categories: AttributeStateCategory[],
  attributeCallbackFn: AttributeCallbackFn<any>,
): AttributeStateCategory[] => {
  const c = categories.map((category) => ({
    ...category,
    subcategories: category.subcategories.map<AttributeStateSubCategory>(
      (subcategory) => ({
        ...subcategory,
        attributes: subcategory.attributes.map<AttributeStateSubCategoryItem>(
          (attribute) => ({
            ...attributeCallbackFn(category, attribute),
          }),
        ),
      }),
    ),
  }));

  return sortCategories(c);
};

/**
 * Combines the values with attributes in specific structure
 * @param category category in which the attribute is located
 * @param attribute specific attribute we're updating
 * @param allGraphicIds ids of all graphics
 * @param groupedGraphicIdsAndAttributesByKey
 * @param attributeCategoryDefinitions
 * @returns Attribute with sorted values
 */
export const combineAttributeValuesInCategories: AttributeCallbackFn<string> = (
  category: AttributeStateCategory,
  attribute: AttributeStateSubCategoryItem,
  allGraphicIds?: string[],
  groupedGraphicIdsAndAttributesByKey?: Record<string, AttributeWithGraphicIds>,
  attributeCategoryDefinitions?: AttributeCategoryDefinitionDto[],
): AttributeStateSubCategoryItem => {
  if (!!!allGraphicIds || !!!groupedGraphicIdsAndAttributesByKey) {
    return { ...attribute };
  }

  // get all attribute values in a range
  const attributeRangeValues = Object.values(
    groupedGraphicIdsAndAttributesByKey,
  ).filter(
    (grouped) =>
      grouped.elementType === category.type &&
      grouped.attributeName === attribute.name,
  );

  // used graphic ids for specific attribute (elements with value for that attribute)
  const usedGraphicIds = new Set(
    attributeRangeValues.flatMap(({ graphicIds }) => graphicIds),
  );

  // rest of the graphic ids (elements without value for that attribute)
  // we get all graphic ids and filter out the used ones
  const unusedGraphicIds = [...allGraphicIds].filter(
    (id) => !usedGraphicIds.has(id),
  );

  // map all range values to specific format
  const mappedRange = attributeRangeValues.map<AttributeRangeValue>(
    ({ value, graphicIds, isVisible, currency, displayValue }) => ({
      value,
      graphicIds,
      isVisible,
      currency,
      valueType: attribute.type ?? 'text',
      valueAlignLeft: attribute.type
        ? !alignRightValueTypes.has(attribute.type)
        : true,
      isColorPickerVisible: false,
      displayValue,
    }),
  );

  const sortedRange = sortAttributes(
    category,
    attribute,
    mappedRange,
    attributeCategoryDefinitions,
  );

  // add noValue item to range
  sortedRange.push({
    ...noValueDefaultRangeData,
    graphicIds: unusedGraphicIds,
  });

  return { ...attribute, range: sortedRange };
};

/**
 * Updates the noValue ranges in the attribute to updated graphicIds based on passed
 * @param _ Category param (isn't used in this function)
 * @param attribute attribute for which we're updating the noValue entry in range array
 * @param graphicIds ids that need to be included in the range
 * @returns updated attribute
 */
export const updateNoValueInAllRanges: AttributeCallbackFn<GraphicIdParam> = (
  _: AttributeStateCategory,
  attribute: AttributeStateSubCategoryItem,
  graphicIds?: GraphicIdParam[],
): AttributeStateSubCategoryItem => ({
  ...attribute,
  range: attribute.range?.map<AttributeRangeValue>((range) => {
    if (range.value !== colorLegendNoValueKey) {
      return { ...range };
    }
    const deletedGraphicIds = graphicIds
      ?.filter((idObj) => idObj.modifyType === 'delete')
      .map((idObj) => idObj.graphicId);
    const allOtherIds = graphicIds
      ?.filter((idObj) => !deletedGraphicIds?.includes(idObj.graphicId))
      .map((idObj) => idObj.graphicId);
    const updatedRange = range.graphicIds.filter(
      (id) => !deletedGraphicIds?.includes(id),
    );
    return {
      ...range,
      graphicIds: mergeArrays(updatedRange, allOtherIds ?? []),
    };
  }),
});

export const sortAttributes = (
  category: AttributeStateCategory,
  attribute: AttributeStateSubCategoryItem,
  range: AttributeRangeValue[],
  attributeCategoryDefinitions?: AttributeCategoryDefinitionDto[],
): AttributeRangeValue[] => {
  // once we're at es2023, we can remove this and use .toSorted on the array
  const copyOfRange = [...range];
  switch (attribute.type) {
    // false -> true
    case 'boolean': {
      const booleanOrder = ['false', 'true'];

      return copyOfRange.sort(
        (a, b) =>
          booleanOrder.indexOf(a.value.toString()) -
          booleanOrder.indexOf(b.value.toString()),
      );
    }

    // older -> newer
    case 'date':
      return copyOfRange.sort(
        (a, b) =>
          new Date(a.value.toString()).getTime() -
          new Date(b.value.toString()).getTime(),
      );

    // lower -> higher
    case 'money':
    case 'integer':
    case 'real':
      return copyOfRange.sort((a, b) => (a.value > b.value ? 1 : -1));

    // a -> z
    case 'reference':
    case 'string':
    case 'text':
      return copyOfRange.sort((a, b) =>
        a.value.toString().localeCompare(b.value.toString()),
      );

    // based on sorted enum array
    case 'enum': {
      // find subcategory
      const subcategory = category.subcategories.find((sub) =>
        sub.attributes.find((atr) => isEqual(atr, attribute)),
      );

      // find attribute category definition based on category and subcategory
      const attributeCategoryDefinition = attributeCategoryDefinitions?.find(
        (acd) => {
          return (
            acd.elementType === category.type && acd.name === subcategory?.name
          );
        },
      );

      // find attribute
      const attr = attributeCategoryDefinition?.attributeDefinitions.find(
        (ad) => ad.name === attribute.name,
      );

      // find array of sorted enums for attribute
      const enumMap = new Map<string, { name: string; order: number }>();
      if (attr?.type === 'enum') {
        for (const [index, value] of attr.enumValues.entries()) {
          enumMap.set(value.id, { name: value.name, order: index });
        }
      }

      // sort range based on sorted enum values
      return copyOfRange
        .sort(
          (a, b) =>
            (enumMap.get(a.value.toString() as UUID)?.order ?? 0) -
            (enumMap.get(b.value.toString() as UUID)?.order ?? 0),
        )
        .map((v) => {
          return {
            ...v,
            // added fallback in case the attribute wasn't loaded or the name isn't set
            value: enumMap.get(v.value.toString() as UUID)?.name ?? v.value,
          };
        });
    }

    default:
      return copyOfRange;
  }
};

export const sortCategories = (
  categories: AttributeStateCategory[],
): AttributeStateCategory[] =>
  categories
    .map((category) => ({
      ...category,
      subcategories: category.subcategories
        .map((subcategory) => ({
          ...subcategory,
          attributes: subcategory.attributes.sort((a, b) =>
            a.name!.localeCompare(b.name!),
          ),
        }))
        .sort((a, b) => a.name!.localeCompare(b.name!)),
    }))
    .sort((a, b) => a.type.localeCompare(b.type));
